import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import ActivityApi from "../../../api/instance/ActivityApi";

import * as dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';

// Helpers.
import Helper from '../../../helpers/Helper';
import { getInstanceExecutions, getInstanceTotalTime } from "./utils";

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import HostTarget from '../../../types/host/HostTarget';
import PieChartSeries from '../../../types/PieChartSeries';
import Wait from '../../../types/instance/Wait';
import Change from '../../../types/instance/Change';
import Event from '../../../types/instance/Event';
import ChartPlotLine from '../../../types/ChartPlotLine'
import WaitsDataTable from '../../../types/instance/tables/WaitsDataTable';
import ChartOption from '../../../types/instance/ChartOption';
import FilterOption from '../../../types/instance/FilterOption';
import GroupedStatement from "../../../types/instance/GroupedStatement";
import { ActivityBatchGroup } from "../../../types/instance/Activity";
import GroupedStatementsDataTable from "../../../types/instance/tables/GroupedStatementsDataTable";
import ClientsDataTable from "../../../types/instance/tables/ClientsDataTable";
import Client from "../../../types/instance/Client";
import DatabasesDataTable from "../../../types/instance/tables/DatabasesDataTable";
import Database from "../../../types/instance/Database";
import SessionsDataTable from "../../../types/instance/tables/SessionsDataTable";
import Session from "../../../types/instance/Session";
import ProgramsDataTable from "../../../types/instance/tables/ProgramsDataTable";
import Program from "../../../types/instance/Program";
import UsersDataTable from "../../../types/instance/tables/UsersDataTable";
import User from "../../../types/instance/User";
import Alert from "../../../types/instance/Alert";

// Constants.
import { CHART_DATA_OPTION, chartOptionsList } from '../../../component/Constants';
import { INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_SQLSERVER } from '../../../component/Constants';
import { DATA_LOADING, DATA_FAILED, DATA_LOADED } from '../../../component/Constants';
import { CHART_COLOURS_STATEMENTS, CHART_COLOURS_WAITS } from '../../../component/Constants';

// Components.
import HostsOverTimeCharts from '../../../component/instance/chart/HostsOverTimeCharts';
import PieChart from '../../../component/PieChart';
import TablesWrapper from "../../../component/instance/table/TablesWrapper";
import ActivityTabs from "../../../component/instance/Tabs";
import GroupedTimeChart from "../../../component/instance/chart/GroupedTimeChart";
import WidgetCard from "../../../component/instance/widget";
import StatementDetailsPanel from "./StatementDetailsPanel";

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(isoWeek.default);

interface IActivityProps {
    instance: InstanceTarget,
    hosts: HostTarget[],
    batchId?: string,
    filterOptions: FilterOption[],
    filterParameters: string,
    instanceId: number,
    chartPlotLines: ChartPlotLine[],
    changes: Change[],
    events: Event[],
    alerts: Alert[],
    setFilterOptions: Function,
}

const chartSeriesCeiling = Number(process.env.REACT_APP_CHART_SERIES_CEILING);

const Activity = (props: IActivityProps) => {
    const [loading, setLoading] = useState(DATA_LOADING);
    const [optionsList, setOptionsList] = useState(chartOptionsList);
    const [pieChartLoading, setPieChartLoading] = useState(true);
    const [totalInstanceTime, setTotalInstanceTime] = useState(0);
    const [totalPreviousInstanceTime, setTotalPreviousInstanceTime] = useState(0);
    const [executions, setExecutions] = useState(0);
    const [previousExecutions, setPreviousExecutions] = useState(0);
    const [timeChartDataOption, setTimeChartDataOption] = useState(CHART_DATA_OPTION.ACTIVITY);
    const [timeChartDataTitle, setTimeChartDataTitle] = useState('Activity Chart');
    const [currentTab, setCurrentTab] = useState('');

    const [displayHostMetrics, setDisplayHostMetrics] = useState<boolean>(false);

    // State for statement details panel
    const [selectedStatement, setSelectedStatement] = useState<string | null>(null);
    const [selectedStatementType, setSelectedStatementType] = useState<'statement' | 'groupedstatement' | 'batch' | 'groupedbatch'>('groupedstatement');
    const [panelOpen, setPanelOpen] = useState(false);

    const [batchesLength, setBatchesLength] = useState<number>(0);

    const [batches, setBatches] = useState<ActivityBatchGroup[]>([]);
    const [statements, setStatements] = useState<GroupedStatement[]>([]);

    const [statementsDataTable, setStatementsDataTable] = useState<GroupedStatementsDataTable[]>([]);
    const [waitsDataTable, setWaitsDataTable] = useState<WaitsDataTable[]>([]);
    const [clientsDataTable, setClientDataTable] = useState<ClientsDataTable[]>([]);
    const [databasesDataTable, setDatabasesDataTable] = useState<DatabasesDataTable[]>([]);
    const [sessionsDataTable, setSessionsDataTable] = useState<SessionsDataTable[]>([]);
    const [programsDataTable, setProgramsDataTable] = useState<ProgramsDataTable[]>([]);
    const [usersDataTable, setUsersDataTable] = useState<UsersDataTable[]>([]);

    const [statementsPieChartSeries, setStatementsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [waitsPieChartSeries, setWaitsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [batchesPieChartSeries, setBatchesPieChartSeries] = useState<PieChartSeries[]>([]);
    const [clientsPieChartSeries, setClientsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [databasesPieChartSeries, setDatabasesPieChartSeries] = useState<PieChartSeries[]>([]);
    const [programsPieChartSeries, setProgramsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [sessionsPieChartSeries, setSessionsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [usersPieChartSeries, setUsersPieChartSeries] = useState<PieChartSeries[]>([]);

    const {
        instance,
        hosts,
        chartPlotLines,
        changes,
        events,
        alerts,
        instanceId,
        setFilterOptions
    } = props;
    const timeRangeContext = useContext(TimeRangeContext);

    useEffect(() => {
        setLoading(DATA_LOADING)
        setPieChartLoading(true)
        void getData();
    }, [instanceId, timeRangeContext, props.filterParameters]);

    useEffect(() => {
        let updatedList = [...chartOptionsList];

        updatedList.splice(3, 0, { label: instance.type === INSTANCE_TYPE_ORACLE ? 'Schemas Chart' : 'Databases Chart', value: CHART_DATA_OPTION.DATABASES });

        if (instance.type === INSTANCE_TYPE_SQLSERVER) {
            updatedList.splice(2, 0, { label: "Grouped Batches Chart", value: CHART_DATA_OPTION.BATCHES });
        }

        // Replace "Statements Chart" with "Grouped Statements Chart"
        const updatedListWithReplacement = updatedList.map(item => {
            if (item.label === 'Statements Chart') {
                return { ...item, label: 'Grouped Statements Chart' };
            }
            return item;
        });

        setOptionsList(updatedListWithReplacement);
    }, [instance.type]);

    const getData = async () => {
        const { instance } = props;
        try {
            setLoading(DATA_LOADING);
            setPieChartLoading(true);
            
            // Reset all data states to ensure fresh data
            setStatements([]);
            setStatementsDataTable([]);
            setStatementsPieChartSeries([]);
            setWaitsDataTable([]);
            setWaitsPieChartSeries([]);
            setClientDataTable([]);
            setClientsPieChartSeries([]);
            setDatabasesDataTable([]);
            setDatabasesPieChartSeries([]);
            setSessionsDataTable([]);
            setSessionsPieChartSeries([]);
            setProgramsDataTable([]);
            setProgramsPieChartSeries([]);
            setUsersDataTable([]);
            setUsersPieChartSeries([]);
            setBatches([]);
            setBatchesPieChartSeries([]);
            setTotalInstanceTime(0);
            setTotalPreviousInstanceTime(0);
            setExecutions(0);
            setPreviousExecutions(0);

            await getStatements(instance.id);
            if (instance.type === INSTANCE_TYPE_SQLSERVER) {
                await getBatches(instanceId);
            }
            await getInstanceTime(instance.id);
            if (!props.filterParameters) {
                await getExecutions(instance.id);
            }
            await getWaits(instance.id);
            await getClients(instance.id);
            await getDatabases(instance.id);
            await getSessions(instance.id);
            await getPrograms(instance.id);
            await getUsers(instance.id);
            
            setLoading(DATA_LOADED);
            setPieChartLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(DATA_FAILED);
            setPieChartLoading(false);
        }
    };


    const getInstanceTime = async (instanceId: number) => {
        const executionsTimeResult = await getInstanceTotalTime(instanceId, timeRangeContext, props.filterParameters)
        setTotalInstanceTime(executionsTimeResult.totalInstanceTime)
        setTotalPreviousInstanceTime(executionsTimeResult.totalPreviousInstanceTime)
    }

    const getExecutions = async (instanceId: number) => {
        const executionsResult = await getInstanceExecutions(instanceId, timeRangeContext, props.filterParameters)
        setExecutions(executionsResult.executions)
        setPreviousExecutions(executionsResult.previousExecutions)
    }

    const getStatements = async (instanceId: number) => {
        try {
            const response = await ActivityApi.getSqlGroup(buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            let statementsDataTable: GroupedStatementsDataTable[] = [],
                    topStatementsChartSeries: PieChartSeries[] = [];

            const sortedData = response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(a.grouphash) || 0;
                const bHash = Number(b.grouphash) || 0;

                return aHash - bHash;
            });

            // Build top statements pie chart.
            for (let index = 0; index < sortedData.length; index++) {

                if (index < CHART_COLOURS_STATEMENTS.length) {
                    // Set the top colours for the chart.
                    sortedData[index].color = CHART_COLOURS_STATEMENTS[index];
                }

                sortedData[index].sqlhash = sortedData[index].grouphash;
                statementsDataTable.push({
                    id: index,
                    color: ((index < CHART_COLOURS_STATEMENTS.length) ? CHART_COLOURS_STATEMENTS[index] : ''),
                    grouphash: sortedData[index].grouphash,
                    sqltext: sortedData[index].sqltext,
                    executions: ((sortedData[index].executions === undefined) ? null : Number(sortedData[index].executions)),
                    waittime: sortedData[index].waittime,
                    averagetime: ((sortedData[index].executions === undefined) ? null : Number((sortedData[index].waittime / Number(sortedData[index].executions)).toFixed(2))),
                    statement: sortedData[index]
                });

                topStatementsChartSeries.push({
                    color: sortedData[index].color,
                    formatted: Helper.getTimeInEnglish(sortedData[index].waittime),
                    name: ((sortedData[index].grouphash !== null) ? sortedData[index].grouphash : '-'),
                    y: sortedData[index].waittime,
                    url: ((sortedData[index].grouphash !== null && sortedData[index].grouphash !== '-') ? `/instances/${instanceId}/activity/groupedstatement/${response.data[index].grouphash}` : null)
                });
            }

            if (sortedData.length > chartSeriesCeiling) {

                // Get and add a remaining category to the chart.
                const remainingTime = sortedData.slice(chartSeriesCeiling, sortedData.length)
                        .map((item: GroupedStatement) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);

                topStatementsChartSeries.push({
                    color: '#5e4fa2',
                    formatted: Helper.getTimeInEnglish(remainingTime),
                    name: 'Other Statements',
                    y: remainingTime,
                    url: null
                });
            }
            setStatementsDataTable(statementsDataTable)
            setStatements(sortedData)
            setStatementsPieChartSeries(topStatementsChartSeries.slice(0, chartSeriesCeiling))
        } catch (error) {
            console.error('Error fetching grouped statements:', error);
            throw error;
        }
    }

    const getBatches = async (instanceId: number) => {
        try {
            const response = await ActivityApi.getBatchGroup(buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            let topBatchesChartSeries: PieChartSeries[] = [];

            response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(a.batchgrouphash) || 0;
                const bHash = Number(b.batchgrouphash) || 0;

                return aHash - bHash;
            });

            // Build top batches pie chart.
            for (let index = 0; index < response.data.length &&
            index < CHART_COLOURS_STATEMENTS.length; index++) {
                topBatchesChartSeries.push({
                    color: CHART_COLOURS_STATEMENTS[index],
                    formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    name: ((response.data[index].batchgrouphash !== null) ? response.data[index].batchgrouphash : '-'),
                    y: response.data[index].waittime,
                    url: ((response.data[index].batchgrouphash !== null && response.data[index].batchgrouphash !== '-') ? `/instances/${instanceId}/activity/groupedbatch/${response.data[index].batchgrouphash}` : null)
                });
            }

            if (response.data.length > chartSeriesCeiling) {

                // Get and add a remaining category to the chart.
                const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                        .map((item: ActivityBatchGroup) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);

                topBatchesChartSeries.push({
                    color: '#5e4fa2',
                    formatted: Helper.getTimeInEnglish(remainingTime),
                    name: 'Other Statements',
                    y: remainingTime,
                    url: null
                });
            }

            setBatches(response.data)
            setBatchesPieChartSeries(topBatchesChartSeries.slice(0, chartSeriesCeiling))
        } catch (error) {
            console.error('Error fetching grouped batches:', error);
            throw error;
        }
    }

    const getWaits = async (instanceId: number) => {
        try {
            const response = await ActivityApi.getWaitEvents(buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            let waitsDataTable: WaitsDataTable[] = [],
                    topWaitsChartSeries: PieChartSeries[] = [];

            response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                return (a.waitevent ?? '').localeCompare(b.waitevent ?? '');
            });

            // Build top waits pie chart.
            for (let index = 0; index < response.data.length; index++) {

                if (index < CHART_COLOURS_WAITS.length) {
                    // Set the top colours for the chart.
                    response.data[index].color = CHART_COLOURS_WAITS[index];
                }

                waitsDataTable.push({
                    id: index,
                    color: response.data[index].color,
                    waitevent: response.data[index].waitevent,
                    waittime: response.data[index].waittime
                });

                topWaitsChartSeries.push({
                    color: response.data[index].color,
                    formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    name: response.data[index].waitevent,
                    y: response.data[index].waittime
                });
            }

            if (response.data.length > chartSeriesCeiling) {

                // Get and add a remaining category to the chart.
                const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                        .map((item: Wait) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);

                topWaitsChartSeries.push({
                    color: CHART_COLOURS_WAITS[chartSeriesCeiling],
                    formatted: Helper.getTimeInEnglish(remainingTime),
                    name: 'Other Waits',
                    y: remainingTime
                });
            }

            setWaitsDataTable(waitsDataTable)
            setWaitsPieChartSeries(topWaitsChartSeries.slice(0, chartSeriesCeiling))
        } catch (error) {
            console.error('Error fetching waits:', error);
            throw error;
        }
    }

    const fetchData = async (endpoint: string, instanceId: number, setDataTable: Function, setPieChartSeries: Function, transformData: (item: any) => { name: string, waittime: number, key: string }) => {
        let data: any[] = [], pieChartSeries: PieChartSeries[] = [];

        try {
            const response = await ActivityApi.getDimension(endpoint, buildApiQuery(timeRangeContext.timeRange), instanceId, props.filterParameters);

            const sortedData = response.data.sort((a: any, b: any) => {
                if (a.waittime !== b.waittime) {
                    return b.waittime - a.waittime;
                }

                const key = transformData(a).key;
                const aName = a[key] ?? '';
                const bName = b[key] ?? '';

                return aName.localeCompare(bName);
            });

            sortedData.forEach((item: any, index: number) => {
                const { name, waittime, key } = transformData(item);

                data.push({
                    id: index,
                    color: CHART_COLOURS_STATEMENTS[index],
                    [key]: name,
                    waittime
                });

                if (index < Number(process.env.REACT_APP_CHART_SERIES_CEILING)) {
                    pieChartSeries.push({
                        color: CHART_COLOURS_STATEMENTS[index],
                        formatted: Helper.getTimeInEnglish(waittime),
                        name,
                        y: waittime
                    });
                }
            });

            setDataTable(data);
            setPieChartSeries(pieChartSeries);
        } catch (error) {
            console.error(`Failed to retrieve ${endpoint}.`, error);
            // Set empty arrays to prevent stale data
            setDataTable([]);
            setPieChartSeries([]);
            throw error;
        }
    };

    const getDatabases = async (instanceId: number) => {
        void fetchData(
                'database',
                instanceId,
                setDatabasesDataTable,
                setDatabasesPieChartSeries,
                (item: Database) => ({
                    name: item.database || '-',
                    waittime: item.waittime,
                    key: 'database'
                })
        );
    };

    const getSessions = async (instanceId: number) => {
        void fetchData(
                'session',
                instanceId,
                setSessionsDataTable,
                setSessionsPieChartSeries,
                (item: Session) => ({
                    name: item.session || '-',
                    waittime: item.waittime ,
                    key: 'session'
                })
        );
    };

    const getPrograms = async (instanceId: number) => {
        void fetchData(
                'program',
                instanceId,
                setProgramsDataTable,
                setProgramsPieChartSeries,
                (item: Program) => ({
                    name: item.program || '-',
                    waittime: item.waittime,
                    key: 'program'
                })
        );
    };

    const getUsers = async (instanceId: number) => {
        void fetchData(
                'username',
                instanceId,
                setUsersDataTable,
                setUsersPieChartSeries,
                (item: User) => ({
                    name: item.username || '-',
                    waittime: item.waittime ,
                    key: 'username'
                })
        );
    };

    const getClients = async (instanceId: number) => {
        void fetchData(
                'client',
                instanceId,
                setClientDataTable,
                setClientsPieChartSeries,
                (item: Client) => ({
                    name: item.client || '-',
                    waittime: item.waittime,
                    key: 'client'
                })
        );
    };

    const toggleTimeChartData = (timeChartDataOption: number) => {
        const selectedOption = optionsList.find(({ value }) => value === timeChartDataOption);
        const timeChartDataTitle = selectedOption?.label || "Unknown Chart";

        setTimeChartDataOption(timeChartDataOption);
        setTimeChartDataTitle(timeChartDataTitle);
    };

    const toggleHostMetricChart = () => {
        setDisplayHostMetrics(!displayHostMetrics)
    }

    const setBatchesOptions = (batchesLength: number, batchesPieChartSeries: PieChartSeries[]) => {
        setBatchesLength(batchesLength)
        setBatchesPieChartSeries(batchesPieChartSeries)
    }

    const openPanel = (statementId: string, statementType: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch' = 'groupedstatement') => {
        setSelectedStatement(statementId);
        setSelectedStatementType(statementType);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedStatement(null);
    };

    let pieChartOptions: ChartOption[] = [
        { optionId: 0, key: 'Grouped Statements', data: statementsPieChartSeries },
    ];

    if (instance.type === INSTANCE_TYPE_SQLSERVER) {
        pieChartOptions.push({ optionId: 0, key: 'Grouped Batches', data: batchesPieChartSeries });
    }

    pieChartOptions.push(
            { optionId: 0, key: 'Waits', data: waitsPieChartSeries },
            { optionId: 0, key: (instance.type === INSTANCE_TYPE_ORACLE) ? 'Schemas' : 'Databases', data: databasesPieChartSeries },
            { optionId: 0, key: 'Sessions', data: sessionsPieChartSeries },
            { optionId: 0, key: 'Clients', data: clientsPieChartSeries },
            { optionId: 0, key: 'Users', data: usersPieChartSeries },
            { optionId: 0, key: 'Programs', data: programsPieChartSeries }
    );

    const pieChartComponents = useMemo(() => {
        return (
                <div className="row row-cols-1 row-cols-lg-2">
                            <PieChart filtersOptions={props.filterOptions} uniqueIndex={0}
                                      chartOptions={pieChartOptions}
                                      loading={pieChartLoading}
                                      instanceType={instance.type}
                                      defaultChartOption={pieChartOptions.filter(option => option.key === 'Grouped Statements')[0]}
                                      setFilterOptions={props.setFilterOptions}/>


                            <PieChart filtersOptions={props.filterOptions} uniqueIndex={1}
                                      chartOptions={pieChartOptions}
                                      instanceType={instance.type}
                                      loading={pieChartLoading}
                                      defaultChartOption={pieChartOptions.filter(option => option.key === 'Waits')[0]}
                                      setFilterOptions={props.setFilterOptions}/>
                </div>
        );
    }, [
        pieChartLoading,
        pieChartOptions,
        props.filterOptions,
        instance.type,
        props.setFilterOptions,
        batchesPieChartSeries,
        clientsPieChartSeries,
        databasesPieChartSeries,
        programsPieChartSeries,
        statementsPieChartSeries,
        sessionsPieChartSeries,
        usersPieChartSeries,
        waitsPieChartSeries,
    ]);

    return (
            <React.Fragment>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 flexlist">
                    <WidgetCard
                        name="Total Time"
                        type='time'
                        iconClassName="time"
                        icon="fa-stopwatch"
                        value={totalInstanceTime}
                        previous={totalPreviousInstanceTime}
                        cardTooltipText="The total time spent"
                    />
                    {!props.filterParameters && (
                        <>
                            <WidgetCard
                                name="Executions"
                                iconClassName="executions"
                                icon="fa-chart-line"
                                value={executions}
                                previous={previousExecutions}
                                cardTooltipText="The total number of executions"
                            />
                            <WidgetCard
                                name="Average Time"
                                type='time'
                                iconClassName="averagetime"
                                icon="fa-clock"
                                value={executions > 0 ? totalInstanceTime / executions : 0}
                                previous={previousExecutions > 0 ? totalPreviousInstanceTime / previousExecutions : undefined}
                                cardTooltipText="The average time per execution"
                            />
                        </>
                    )}
                    <WidgetCard
                        name="Events"
                        events={{
                            alerts: alerts.length,
                            changes: changes.length,
                            custom: events.length
                        }}
                    />
                </div>

                {/* Main Content Widgets */}
                <div className="row">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                                Time spent
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapseExecutions" aria-expanded="false"
                                   aria-controls="collapseExecutions"/>
                                <div className="btn-group float-end">
                                    {hosts.length > 0 && (
                                            <button type="button" className="btn btn-xsm btn-primary float-end ms-1"
                                                    onClick={() => toggleHostMetricChart()}>
                                                {((displayHostMetrics) ? 'Hide Hosts' : 'Show Hosts')}
                                            </button>
                                    )}

                                    <button id="btnChartList" type="button"
                                            className="btn btn-xsm btn-primary dropdown-toggle float-end ms-1"
                                            data-bs-toggle="dropdown" aria-expanded="false">
                                        {timeChartDataTitle}
                                    </button>
                                    <ul className="dropdown-menu border" aria-labelledby="btnChartList">
                                        <li>
                                            {optionsList.map(({ label, value }) => (
                                                    <span
                                                            key={value}
                                                            className={`dropdown-item ${timeChartDataOption === value ? "active" : ""}`}
                                                            role="button"
                                                            onClick={() => toggleTimeChartData(value)}
                                                    >
                    {label}
                </span>
                                            ))}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div id="collapseExecutions" className="card-body collapse show">
                                <GroupedTimeChart dataOption={timeChartDataOption}
                                                  instanceId={instance.id}
                                                  plotLines={chartPlotLines}
                                                  waits={waitsDataTable}
                                                  statements={statements}
                                                  batches={batches}
                                                  clients={clientsDataTable}
                                                  programs={programsDataTable}
                                                  sessions={sessionsDataTable}
                                                  databases={databasesDataTable}
                                                  users={usersDataTable}
                                                 
                                                  filters={props.filterParameters}/>
                                {displayHostMetrics && (
                                        <HostsOverTimeCharts instance={instance} hosts={hosts}
                                                            
                                                             plotLines={chartPlotLines}/>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {pieChartComponents}

                <div className="row row-cols-1">

                    {/* Tab Headings */}
                    <ActivityTabs
                            instanceType={instance.type}
                            batchesLength={batchesLength}
                            waitsLength={waitsDataTable.length}
                            clientsLength={clientsDataTable.length}
                            databasesLength={databasesDataTable.length}
                            sessionsLength={sessionsDataTable.length}
                            setCurrentTab={setCurrentTab}
                            usersLength={usersDataTable.length}
                            programsLength={programsDataTable.length}
                            statementsLength={statementsDataTable.length}
                            isGroup={true}
                            filterOptions={props.filterOptions}
                    />

                    <TablesWrapper isGroup={true}
                                   instance={instance}
                                   loading={loading}
                                   totalInstanceTime={totalInstanceTime}
                                   statementsDataTable={statementsDataTable}
                                   clientsDataTable={clientsDataTable}
                                   currentTab={currentTab}
                                   databasesDataTable={databasesDataTable}
                                   sessionsDataTable={sessionsDataTable}
                                   programsDataTable={programsDataTable}
                                   usersDataTable={usersDataTable}
                                   waitsDataTable={waitsDataTable}
                                   setFilterOptions={setFilterOptions}
                                   setBatchesOptions={setBatchesOptions}
                                   onStatementClick={openPanel}
                    />
                </div>

                {/* Statement Details Slide-out Panel */}
                {selectedStatement && (
                    <StatementDetailsPanel
                        key={selectedStatement}
                        statementId={selectedStatement}
                        open={panelOpen}
                        instance={instance}
                        onClose={closePanel}
                        statementType={selectedStatementType}
                        onStatementClick={openPanel}
                    />
                )}

            </React.Fragment>
    );
};

Activity.propTypes = {
    setFilterOptions: PropTypes.func.isRequired
};

export default Activity;
